/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { genders, relationships as relationshipOptions } from '../../../components/modal-forms/create-user/Constants';
import GetAllUsers from '../../../hooks/fetch-hooks/fetch-all/get-all-users';
import { UpdateOrder } from '../../../hooks/update-hooks/edit-order/order-data-update';
import {
    getMappingIdFromCreateResponse,
    submitUserPatientMapping,
} from '../../../hooks/update-hooks/map-user-patient/create-map-user-patient';
import { toast } from 'react-toastify';
import { isObject } from 'lodash';
import { TbArrowsRightLeft } from 'react-icons/tb';

const REL_ALL = relationshipOptions.filter((r) => r.label && r.label !== 'Select');

const fieldControlClass =
    'w-full rounded-lg !border-2 !border-solid !border-gray-400 bg-white-50 p-2 text-sm focus:!border-elm-500 focus:outline-none focus:ring-2 focus:ring-elm-500/25 disabled:opacity-60';

function isSelfMapping(m) {
    if (!m?.User || !m?.Patient) return false;
    if (m.relationship === 'SELF') return true;
    return String(m.User.id) === String(m.Patient.id);
}

function normalizeDob(d) {
    if (!d) return '';
    const s = String(d);
    return s.length >= 10 ? s.slice(0, 10) : s;
}

function userFromApiRow(user) {
    return {
        resolvedId: user.id,
        phone: user.contact_no || '',
        name: user.name || '',
        gender: user.gender || 'M',
        dob: normalizeDob(user.dob),
    };
}

function CurrentMappingSummary({ mapUserPatient }) {
    if (!mapUserPatient?.User || !mapUserPatient?.Patient) return null;
    const rel =
        mapUserPatient.relationship || (isSelfMapping(mapUserPatient) ? 'SELF' : '');
    return (
        <div className="rounded-xl border border-elm-200 bg-elm-50 px-4 py-3 mb-4">
            <p className="text-xs font-semibold text-elm-800 uppercase tracking-wide mb-2">Current order mapping</p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                <div>
                    <span className="text-gray-500">Patient</span>
                    <p className="font-medium text-gray-900">{mapUserPatient.Patient.name}</p>
                    <p className="text-gray-600 text-xs">{mapUserPatient.Patient.contact_no}</p>
                </div>
                <div>
                    <span className="text-gray-500">Caregiver (ordering user)</span>
                    <p className="font-medium text-gray-900">{mapUserPatient.User.name}</p>
                    <p className="text-gray-600 text-xs">{mapUserPatient.User.contact_no}</p>
                </div>
                <div>
                    <span className="text-gray-500">Relationship</span>
                    <p className="font-medium text-gray-900">{rel}</p>
                </div>
            </div>
        </div>
    );
}

/** One person: phone doubles as search; picking a row fills id + fields. */
function PersonFields({
    title,
    subtitle,
    updateChecked,
    onUpdateCheckedChange,
    disabledPanel,
    form,
    setForm,
    searchKey,
    usersData,
}) {
    const [filteredUsers, setFilteredUsers] = useState([]);

    useEffect(() => {
        if (form.stopFetch) {
            setFilteredUsers([]);
            return;
        }
        if (usersData?.data?.data?.rows) {
            setFilteredUsers(
                usersData.data.data.rows.map((user) => ({
                    label: user.name,
                    id: user.id,
                    contact_no: user.contact_no,
                    raw: user,
                }))
            );
        }
    }, [usersData, form.stopFetch]);

    const handlePhoneChange = (e) => {
        const rawValue = e.target.value;
        const v = /^\d+$/.test(rawValue) ? rawValue.slice(0, 10) : rawValue;
        setForm((prev) => ({
            ...prev,
            phone: v,
            stopFetch: false,
            resolvedId: null,
        }));
    };

    const pickUser = (row) => {
        const u = userFromApiRow(row.raw || row);
        setFilteredUsers([]);
        setForm((prev) => ({
            ...prev,
            ...u,
            stopFetch: true,
        }));
    };

    const showDropdown =
        !form.stopFetch &&
        filteredUsers.length > 0 &&
        form.phone.trim().length > 1 &&
        !disabledPanel;

    return (
        <div
            className={`rounded-xl border border-gray-200 p-4 flex flex-col gap-3 ${disabledPanel ? 'pointer-events-none bg-gray-50' : 'bg-white-50'
                }`}
        >
            <div className="flex flex-row items-start justify-between gap-2">
                <div>
                    <p className="text-sm font-semibold text-gray-900">{title}</p>
                    <p className="text-xs text-gray-500">{subtitle}</p>
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-700 shrink-0 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={updateChecked}
                        onChange={(e) => onUpdateCheckedChange(e.target.checked)}
                        className="rounded border-gray-300"
                    />
                    Update
                </label>
            </div>

            <div className="relative">
                <label className="block text-xs font-medium text-gray-600 mb-1">Phone (search by number or name)</label>
                <div
                    className="rounded-lg bg-white shadow-sm transition-shadow focus-within:shadow-[0_0_0_2px_rgba(40,164,139,0.35)]"
                    style={{
                        border: '2px solid #9ca3af',
                        borderRadius: '0.5rem',
                        boxSizing: 'border-box',
                    }}
                >
                    <input
                        type="text"
                        inputMode="text"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        name={`upm-user-search-${searchKey}`}
                        className="block w-full min-h-[2.5rem] border-0 bg-transparent px-2 py-2 text-sm text-gray-900 outline-none ring-0 focus:ring-0 disabled:cursor-not-allowed disabled:opacity-60"
                        style={{ WebkitAppearance: 'none', appearance: 'none' }}
                        value={form.phone}
                        onChange={handlePhoneChange}
                        disabled={disabledPanel || !updateChecked}
                        placeholder="10-digit phone or type name, then pick a match"
                    />
                </div>
                {showDropdown && updateChecked && (
                    <div className="absolute z-[300] left-0 right-0 top-full mt-1 rounded-lg border border-gray-200 bg-white-50 shadow-lg overflow-hidden">
                        <ul className="max-h-40 overflow-y-auto bg-white-50">
                            {filteredUsers.map((val) => (
                                <li
                                    key={val.id}
                                    className="cursor-pointer py-2 px-2 hover:bg-gray-100 text-sm"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => pickUser(val)}
                                >
                                    <b>{val.id}</b>: {val.label} — {val.contact_no}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                <input
                    type="text"
                    className={fieldControlClass}
                    value={form.name}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                    disabled={disabledPanel || !updateChecked}
                />
            </div>

            <div className="grid grid-cols-2 gap-2">
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Gender</label>
                    <select
                        className={fieldControlClass}
                        value={form.gender}
                        onChange={(e) => setForm((p) => ({ ...p, gender: e.target.value }))}
                        disabled={disabledPanel || !updateChecked}
                    >
                        {genders.map((g) => (
                            <option key={g.label} value={g.label}>
                                {g.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">DOB</label>
                    <input
                        type="date"
                        className={fieldControlClass}
                        value={form.dob}
                        onChange={(e) => setForm((p) => ({ ...p, dob: e.target.value }))}
                        disabled={disabledPanel || !updateChecked}
                    />
                </div>
            </div>
        </div>
    );
}

function buildPart(form, update, existing, requireFields) {
    if (!update) {
        return { id: existing.id };
    }
    if (requireFields) {
        if (!form.name?.trim() || !form.phone?.trim() || !form.gender || !form.dob) {
            return null;
        }
    }
    if (form.resolvedId) {
        return {
            id: form.resolvedId,
            name: form.name.trim(),
            contact_no: form.phone.trim(),
            gender: form.gender,
            dob: form.dob,
        };
    }
    return {
        name: form.name.trim(),
        contact_no: form.phone.trim(),
        gender: form.gender,
        dob: form.dob,
        source: 'Whatsapp',
    };
}

const emptyForm = () => ({
    resolvedId: null,
    phone: '',
    name: '',
    gender: 'M',
    dob: '1960-01-01',
    stopFetch: false,
});

function trimStr(s) {
    return String(s ?? '').trim();
}

/**
 * True when both columns show the same person on the form (phone, name, gender, DOB).
 * Intentionally ignores `resolvedId`: e.g. “Copy caregiver → patient” clears patient’s id so a new
 * user can be created, but the visible fields still match — swap/copy should treat sides as aligned.
 */
function formsEffectivelySame(cg, pt) {
    return (
        trimStr(cg.phone) === trimStr(pt.phone) &&
        trimStr(cg.name) === trimStr(pt.name) &&
        trimStr(cg.gender) === trimStr(pt.gender) &&
        trimStr(cg.dob) === trimStr(pt.dob)
    );
}

/** Drive `GetAllUsers`: name after 2+ chars; phone only when 10 digits (matches other modals). */
function searchQueryForInput(input) {
    const t = (input || '').trim();
    if (/^\d+$/.test(t)) {
        if (t.length === 10) return { value: t, type: 'contact_no' };
        return { value: '', type: 'name' };
    }
    if (t.length < 2) return { value: '', type: 'name' };
    return { value: t, type: 'name' };
}

const UserPatientModal = ({ open, onClose, orderId, mappingId, mapUserPatient, refetch }) => {
    const [updateCaregiver, setUpdateCaregiver] = useState(true);
    const [updatePatient, setUpdatePatient] = useState(true);
    const [caregiverForm, setCaregiverForm] = useState(emptyForm);
    const [patientForm, setPatientForm] = useState(emptyForm);
    const [relationship, setRelationship] = useState('OTHER');
    const [submitting, setSubmitting] = useState(false);

    const cgQ = useMemo(() => searchQueryForInput(caregiverForm.phone), [caregiverForm.phone]);
    const ptQ = useMemo(() => searchQueryForInput(patientForm.phone), [patientForm.phone]);
    const { data: cgUsers } = GetAllUsers(cgQ.value, cgQ.type);
    const { data: ptUsers } = GetAllUsers(ptQ.value, ptQ.type);

    const resetFromOrder = useCallback(() => {
        if (!mapUserPatient?.User || !mapUserPatient?.Patient) return;
        const u = userFromApiRow(mapUserPatient.User);
        const p = userFromApiRow(mapUserPatient.Patient);
        setCaregiverForm({ ...u, stopFetch: true });
        setPatientForm({ ...p, stopFetch: true });
        const rawRel = mapUserPatient.relationship || (isSelfMapping(mapUserPatient) ? 'SELF' : 'OTHER');
        const rel = REL_ALL.some((x) => x.label === rawRel) ? rawRel : 'OTHER';
        setRelationship(rel);
        setUpdatePatient(true);
        setUpdateCaregiver(rel !== 'SELF');
    }, [mapUserPatient]);

    useEffect(() => {
        if (open) {
            resetFromOrder();
        }
    }, [open, resetFromOrder]);

    useEffect(() => {
        if (relationship === 'SELF') {
            setCaregiverForm((prev) => {
                const next = {
                    ...prev,
                    phone: patientForm.phone,
                    name: patientForm.name,
                    gender: patientForm.gender,
                    dob: patientForm.dob,
                    stopFetch: true,
                };
                if (
                    prev.phone === next.phone &&
                    prev.name === next.name &&
                    prev.gender === next.gender &&
                    prev.dob === next.dob
                ) {
                    return prev;
                }
                return next;
            });
        }
    }, [relationship, patientForm.phone, patientForm.name, patientForm.gender, patientForm.dob]);

    const swapCaregiverAndPatient = () => {
        const nextCg = { ...patientForm, stopFetch: true };
        const nextPt = { ...caregiverForm, stopFetch: true };
        setCaregiverForm(nextCg);
        setPatientForm(nextPt);
        const cgU = updateCaregiver;
        const ptU = updatePatient;
        setUpdateCaregiver(ptU);
        setUpdatePatient(cgU);
    };

    const copyPatientToCaregiver = () => {
        setRelationship('SELF');
        setUpdateCaregiver(false);
        setCaregiverForm({
            ...patientForm,
            resolvedId: null,
            stopFetch: true,
        });
        toast.info('Caregiver column filled from patient — adjust phone or pick a user if this should be a new account.');
    };

    const copyCaregiverToPatient = () => {
        setRelationship('SELF');
        setUpdateCaregiver(false);
        setPatientForm({
            ...caregiverForm,
            resolvedId: null,
            stopFetch: true,
        });
        setUpdatePatient(true);
        toast.info('Patient column filled from caregiver — adjust as needed.');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!mapUserPatient?.User || !mapUserPatient?.Patient) {
            toast.error('Order mapping data is missing');
            return;
        }
        if (!updateCaregiver && !updatePatient) {
            toast.error('Check “Update” on at least caregiver or patient.');
            return;
        }

        const rel = relationship;
        if (!rel) {
            toast.error('Select a relationship');
            return;
        }

        setSubmitting(true);
        try {
            let payload;

            if (rel === 'SELF') {
                if (!formsEffectivelySame(caregiverForm, patientForm)) {
                    toast.error(
                        'SELF means one account for both roles. Make patient and caregiver identical, or use Copy / swap first.'
                    );
                    setSubmitting(false);
                    return;
                }
                const userPart = buildPart(patientForm, updatePatient, mapUserPatient.Patient, true);
                if (!userPart) {
                    toast.warn('Fill patient name, phone, gender, and DOB.');
                    setSubmitting(false);
                    return;
                }
                payload = {
                    user: userPart,
                    patient: userPart,
                    relationship: 'SELF',
                };
            } else {
                const userPart = buildPart(caregiverForm, updateCaregiver, mapUserPatient.User, true);
                const patientPart = buildPart(patientForm, updatePatient, mapUserPatient.Patient, true);
                if (!userPart || !patientPart) {
                    toast.warn('Fill all fields for every profile you are updating.');
                    setSubmitting(false);
                    return;
                }
                if (userPart.id && patientPart.id && String(userPart.id) === String(patientPart.id)) {
                    toast.error('Caregiver and patient must be different users for a non–self relationship.');
                    setSubmitting(false);
                    return;
                }
                payload = {
                    user: userPart,
                    patient: patientPart,
                    relationship: rel,
                };
            }

            const res = await submitUserPatientMapping(orderId, payload);
            if (res?.data?.success === false || (res?.data?.error && !res?.data?.success)) {
                toast.error(
                    !isObject(res?.data?.error) ? String(res?.data?.error) : 'Could not save mapping'
                );
                setSubmitting(false);
                return;
            }

            const newMappingId = getMappingIdFromCreateResponse(res);
            if (newMappingId && String(newMappingId) !== String(mappingId)) {
                const updateRes = await UpdateOrder({ userMappingId: parseInt(newMappingId, 10) }, orderId);
                if (!updateRes?.data?.success) {
                    toast.error(updateRes?.data?.error || 'Mapping saved but order could not be updated');
                    setSubmitting(false);
                    return;
                }
            }

            toast.success('Mapping saved');
            refetch();
            onClose();
        } catch {
            toast.error('Request failed');
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) {
        return null;
    }

    const caregiverPanelDisabled = relationship === 'SELF';
    const sidesMatch = formsEffectivelySame(caregiverForm, patientForm);
    const showSwapButton = !sidesMatch;
    const copyButtonsDisabled = sidesMatch;

    return (
        <div
            id="user-patient-modal-backdrop"
            role="presentation"
            onClick={(e) => {
                if (e.target.id === 'user-patient-modal-backdrop') {
                    onClose();
                }
            }}
            className="w-[100vw] h-[100vh] fixed top-0 left-0 z-[200000] bg-black/50 flex justify-center items-center p-4"
        >
            <div
                className="rounded-lg bg-white-50 p-5 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col shadow-lg ring-1 ring-gray-200"
                role="dialog"
                aria-labelledby="user-patient-modal-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex flex-row justify-between items-center mb-3">
                    <strong id="user-patient-modal-title" className="text-xl">
                        User & patient mapping
                    </strong>
                    <button type="button" onClick={onClose} className="text-gray-600 hover:text-gray-900 p-1" aria-label="Close">
                        ✕
                    </button>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                    Choose which side you are changing, search by phone or name to attach an existing account, or type new
                    details. Set the relationship, then save.
                </p>
                <hr />
                <CurrentMappingSummary mapUserPatient={mapUserPatient} />

                <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 overflow-y-auto bg-white-50">

                    <div className="flex flex-wrap gap-2 mb-3 items-center justify-between w-full">
                        <button
                            type="button"
                            onClick={copyPatientToCaregiver}
                            disabled={copyButtonsDisabled}
                            className="text-sm px-3 py-1.5 rounded-lg border border-elm-300 bg-elm-50 text-elm-900 enabled:hover:bg-elm-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Copy patient → caregiver
                        </button>
                        <button
                            type="button"
                            onClick={copyCaregiverToPatient}
                            disabled={copyButtonsDisabled}
                            className="text-sm px-3 py-1.5 rounded-lg border border-elm-300 bg-elm-50 text-elm-900 enabled:hover:bg-elm-100 disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                        >
                            Copy patient ← caregiver
                        </button>
                    </div>
                    <div className="flex flex-col lg:flex-row gap-4 mb-4 items-stretch">
                        <div className="flex-1 min-w-0">
                            <PersonFields
                                title="Patient"
                                subtitle="Who the order is for"
                                updateChecked={updatePatient}
                                onUpdateCheckedChange={setUpdatePatient}
                                disabledPanel={false}
                                form={patientForm}
                                setForm={setPatientForm}
                                searchKey="pt"
                                usersData={ptUsers}
                            />
                        </div>
                        {showSwapButton && (
                            <div className="flex flex-row lg:flex-col justify-center items-center shrink-0 self-center lg:self-stretch lg:justify-center lg:py-6">
                                <button
                                    type="button"
                                    onClick={swapCaregiverAndPatient}
                                    className="inline-flex items-center justify-center p-2 rounded-full border border-gray-300 bg-white-50 text-elm-700 hover:bg-elm-50 hover:border-elm-300 shadow-sm transition-colors"
                                    aria-label="Swap patient and caregiver"
                                    title="Swap patient and caregiver"
                                >
                                    <TbArrowsRightLeft className="w-5 h-5" aria-hidden />
                                </button>
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <PersonFields
                                title="Caregiver (ordering user)"
                                subtitle="Who places the order"
                                updateChecked={updateCaregiver}
                                onUpdateCheckedChange={setUpdateCaregiver}
                                disabledPanel={caregiverPanelDisabled}
                                form={caregiverForm}
                                setForm={setCaregiverForm}
                                searchKey="cg"
                                usersData={cgUsers}
                            />
                        </div>
                    </div>

                    {relationship === 'SELF' && (
                        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                            Self relationship uses the patient column only; caregiver fields are ignored for this save.
                        </p>
                    )}

                    <div className="border border-gray-200 bg-white-50 rounded-xl p-4 mb-4">
                        <label className="block text-xs font-semibold text-gray-700 mb-2">Relationship</label>
                        <select
                            className="w-full rounded-lg border border-gray-200 bg-white-50 p-2.5 text-sm"
                            value={relationship}
                            onChange={(e) => {
                                const v = e.target.value;
                                const prev = relationship;
                                setRelationship(v);
                                if (v === 'SELF') {
                                    setUpdateCaregiver(false);
                                    if (prev !== 'SELF') {
                                        setCaregiverForm({
                                            ...patientForm,
                                            stopFetch: true,
                                        });
                                    }
                                } else {
                                    setUpdateCaregiver(true);
                                    if (
                                        prev === 'SELF' &&
                                        v !== 'SELF' &&
                                        formsEffectivelySame(caregiverForm, patientForm)
                                    ) {
                                        setCaregiverForm(emptyForm());
                                        toast.info(
                                            'Caregiver fields cleared — add a different caregiver for this relationship.'
                                        );
                                    }
                                }
                            }}
                        >
                            {REL_ALL.map((r) => (
                                <option key={r.label} value={r.label}>
                                    {r.label}
                                </option>
                            ))}
                        </select>
                        <p className="mt-2 text-sm text-gray-600">
                            <span className="font-semibold text-gray-900">{patientForm.name?.trim() || 'Patient'}</span>
                            {' is '}
                            <span className="font-semibold text-elm-700">{relationship || '___'}</span>
                            {' of '}
                            <span className="font-semibold text-gray-900">{caregiverForm.name?.trim() || 'Caregiver'}</span>
                        </p>
                    </div>

                    <button
                        type="submit"
                        disabled={submitting}
                        className="w-full py-3 rounded-lg bg-elm-500 text-white-50 font-medium disabled:opacity-50 mb-2"
                    >
                        {submitting ? 'Saving…' : 'Save mapping'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default UserPatientModal;
